import * as React from 'react';

import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { cn } from '../../../lib/utils';
import { HOUR_PRESETS, MAX_HOUR } from './constants';

const pad = (h: number) => String(h).padStart(2, '0');

export interface HourInputProps {
  /** Selected hour, 0–23. */
  hour: number;
  onChange: (hour: number) => void;
  /** Lowest selectable hour (default 0) — the caller clamps this per selected
   *  day, e.g. "today" only allows hours >= the current hour. */
  minHour?: number;
  /** Highest selectable hour (default MAX_HOUR) — mirrors minHour for the
   *  far end of the picker's date range. */
  maxHour?: number;
}

/**
 * Hour-only (24h) selector: a typed field plus quick-pick presets. The field keeps
 * its own raw text so multi-digit entry works (a padded value would swallow "20").
 */
export function HourInput({ hour, onChange, minHour = 0, maxHour = MAX_HOUR }: HourInputProps) {
  const [text, setText] = React.useState(() => pad(hour));
  // Tracks the last hour value WE told the parent to adopt (or the initial
  // one) — not merely "the previous render's hour". Comparing `hour` to this
  // (React's "adjusting state when a prop changes" pattern) instead of to
  // `parseInt(text)` means a transient empty buffer — cleared by the user but
  // not yet re-typed, so it never called `onChange` and `hour` hasn't moved —
  // isn't mistaken for a mismatch needing correction and snapped back before
  // a fresh digit can be typed. Only a genuinely external hour (a preset
  // click, or the picker resetting elsewhere) differs from this and triggers
  // a resync. State, not a ref: `react-hooks/refs` forbids reading/writing a
  // ref during render.
  const [lastKnownHour, setLastKnownHour] = React.useState(hour);
  if (hour !== lastKnownHour) {
    setLastKnownHour(hour);
    setText(pad(hour));
  }

  const handleChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 2);
    if (digits === '') {
      setText(digits);
      return;
    }
    const next = Math.min(maxHour, Math.max(minHour, parseInt(digits, 10)));
    setLastKnownHour(next);
    onChange(next);
    // Once a complete 2-digit entry lands, show the clamped/padded value —
    // the field must always display what's actually being applied, not the
    // raw out-of-range digits (e.g. typing "24" shows "23", not "24").
    setText(digits.length === 2 ? pad(next) : digits);
  };

  return (
    <>
      <div className="flex flex-col items-center gap-1.5">
        <div className="flex items-center gap-2">
          <Input
            aria-label="Hour"
            inputMode="numeric"
            value={text}
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={() => setText(pad(hour))}
            className="text-heading-h6 h-11 w-16 text-center font-semibold tabular-nums"
          />
          <span className="text-heading-h6 text-muted-foreground font-semibold">:00</span>
        </div>
        <span className="text-caption-regular text-muted-foreground">Hour (00–23)</span>
      </div>

      <div className="flex flex-wrap justify-center gap-1.5">
        {HOUR_PRESETS.filter((h) => h >= minHour && h <= maxHour).map((h) => (
          <Button
            key={h}
            type="button"
            size="xs"
            variant="outline"
            // Selected: softened-black fill with explicit white text (else the xs
            // typography class wins the twMerge color race) and a transparent border.
            className={cn(
              'rounded-full',
              h === hour && 'bg-primary/85 hover:bg-primary/80 border-transparent text-white',
            )}
            onClick={() => onChange(h)}
          >
            {pad(h)}:00
          </Button>
        ))}
      </div>
    </>
  );
}
